import { useEffect } from 'react';
import type { NavBarTab } from '../screens/components/composite/nav/nav_bar/NavBar';
import { SyncWarningBanner } from '../components/SyncWarningBanner';
import { ActionErrorBanner } from '../components/ActionErrorBanner';
import { pickNextPoolGiantToPreload } from '../../domain';
import { preloadSessionLevelAssets } from '../assets/preloadSessionLevelAssets';
import { useAppStore } from '../store/appStore';
import { settleLevelRunTerminal, beginReplayDelivery, settleActiveLevelAbortedIfNeeded } from '../store/settleLevelRun';
import { clearLevelAttempt } from '../store/levelRunSession';
import { isCourierId } from '../../domain/ids/ids';
import { setBackgroundMusic } from '../assets/preloadSounds';
import { DailyStrikeScreen } from '../screens/daily_strike_screen/DailyStrikeScreen';
import { DeliveryListScreen } from '../screens/delivery_list_screen/DeliveryListScreen';
import { DeliveryResultScreen } from '../screens/delivery_result_screen/DeliveryResultScreen';
import { FailDeliveryScreen } from '../screens/fail_delivery_screen/FailDeliveryScreen';
import { LevelScreen } from '../screens/level_screen/LevelScreen';
import { MapScreen } from '../screens/map_screen/MapScreen';
import { NewDeliveryScreen } from '../screens/new_delivery_screen/NewDeliveryScreen';
import { PauseScreen } from '../screens/pause_screen/PauseScreen';
import { PlayerLevelScreen } from '../screens/player_level_screen/PlayerLevelScreen';
import { QuizScreen } from '../screens/quiz_screen/QuizScreen';
import { RatingScreen } from '../screens/rating_screen/RatingScreen';
import { SelectCharacterScreen } from '../screens/select_character_screen/SelectCharacterScreen';
import { StickerRewardScreen } from '../screens/sticker_reward_screen/StickerRewardScreen';
import { StickersScreen } from '../screens/stickers_screen/StickersScreen';
import { SuccessDeliveryScreen } from '../screens/success_delivery_screen/SuccessDeliveryScreen';
import { TaskListScreen } from '../screens/task_list_screen/TaskListScreen';
import { TaskDetailScreen } from '../screens/task_detail_screen/TaskDetailScreen';
import { TicketsScreen } from '../screens/tickets_screen/TicketsScreen';
import { LoadingScreen } from '../screens/loading_screen/LoadingScreen';
import { WelcomeDelivery1 } from '../screens/welcome_delivery_1/WelcomeDelivery1';
import { WelcomeDelivery2 } from '../screens/welcome_delivery_2/WelcomeDelivery2';
import {
  HUB_TAB_TO_SCREEN,
  TASK_INDEX_QUIZ,
  taskKindFromIndex,
  type ScreenEntry,
} from './screenIds';
import {
  nextScreenAfterClaimSticker,
  nextScreenAfterViewResults,
  resolveStickerRewardScreen,
} from './stickerRewardNavigation';
import {
  useScreenNavigation,
  type ScreenNavigation,
} from './useScreenNavigation';

function handleHubTabChange(nav: ScreenNavigation, tab: NavBarTab) {
  nav.replaceRoot(HUB_TAB_TO_SCREEN[tab]);
}

type ScreenRenderContext = {
  passesBalance: number;
  totalRating: number;
};

function renderScreen(
  entry: ScreenEntry,
  nav: ScreenNavigation,
  ctx: ScreenRenderContext,
) {
  const { id, params } = entry;
  const giantId = params?.giantId;
  const characterId = params?.characterId;
  const levelKey = params?.levelKey ?? 0;
  const orderIndex = params?.orderIndex ?? 0;
  const revealItem = params?.revealItem === true;
  const rewardPasses = params?.rewardPasses ?? 0;
  const unlockedStickers = params?.unlockedStickers;
  const stickerId = params?.stickerId;
  const remainingStickers = params?.remainingStickers;
  const deliveryParams = { giantId, characterId, orderIndex };

  const goHomeFromLevel = () => {
    void settleActiveLevelAbortedIfNeeded().then(() => {
      clearLevelAttempt();
      nav.replaceRoot('map');
    });
  };

  const restartLevel = () => {
    if (giantId == null) {
      return;
    }
    void settleActiveLevelAbortedIfNeeded()
      .then(() => beginReplayDelivery(giantId, orderIndex))
      .then((ok) => {
        if (!ok) {
          return;
        }
        clearLevelAttempt();
        nav.replace('selectCharacter', { giantId, orderIndex });
      });
  };

  switch (id) {
    case 'loading':
      return (
        <LoadingScreen
          onContinue={() => {
            if (useAppStore.getState().onboardingComplete) {
              nav.replaceRoot('map');
              return;
            }
            nav.replace('welcome1');
          }}
        />
      );
    case 'welcome1':
      return (
        <WelcomeDelivery1
          onContinue={() => {
            nav.replace('welcome2');
          }}
        />
      );
    case 'welcome2':
      return (
        <WelcomeDelivery2
          onContinue={() => {
            void useAppStore.getState().completeOnboarding().then(() => {
              nav.replaceRoot('map');
            });
          }}
        />
      );
    case 'map':
      return (
        <MapScreen
          activeTab="map"
          passesBalance={ctx.passesBalance}
          onTabChange={(tab) => handleHubTabChange(nav, tab)}
          onTakeDelivery={(giantId, orderIndex) =>
            nav.push('newDelivery', { giantId, orderIndex })
          }
          onOpenTickets={() => nav.push('taskList')}
          onOpenRating={() => nav.push('rating')}
          onOpenPlayerLevel={() => nav.push('playerLevel')}
        />
      );
    case 'newDelivery':
      return giantId != null ? (
        <NewDeliveryScreen
          giantId={giantId}
          orderIndex={orderIndex}
          revealItem={revealItem}
          onGoBack={() => nav.goBack()}
          onTakeOrder={() => {
            if (giantId == null) {
              return;
            }
            const missionOrderIndex = orderIndex ?? 0;
            void useAppStore
              .getState()
              .ensureAndBeginDelivery({ giantId, orderIndex: missionOrderIndex })
              .then((result) => {
                if (result.ok) {
                  nav.push('selectCharacter', {
                    giantId,
                    orderIndex: missionOrderIndex,
                  });
                }
              });
          }}
        />
      ) : null;
    case 'selectCharacter':
      return (
        <SelectCharacterScreen
          onGoBack={() => nav.goBack()}
          onConfirm={(id) => {
            if (isCourierId(id)) {
              void useAppStore.getState().setSelectedCourier(id);
            }
            nav.replace('level', {
              giantId,
              characterId: id,
              levelKey: 0,
              orderIndex,
            });
          }}
        />
      );
    case 'level':
      return giantId != null ? (
        <LevelScreen
          key={`level-${giantId}-${characterId}-${levelKey}`}
          giantId={giantId}
          characterId={characterId}
          orderIndex={orderIndex}
          levelKey={levelKey}
          isPaused={nav.overlay?.id === 'pause'}
          onPause={() => nav.openOverlay('pause')}
          onSuccess={(snapshot) => {
            void settleLevelRunTerminal({
              giantId,
              orderIndex,
              outcome: 'completed',
              snapshot,
            }).then((result) => {
              const pool = useAppStore.getState().playerEconomy.deliveryPool;
              const next = pickNextPoolGiantToPreload(pool, null);
              if (next != null) {
                void preloadSessionLevelAssets([next], { limit: 1 });
              }
              nav.replace('successDelivery', {
                ...deliveryParams,
                unlockedStickers: result.unlockedStickers,
              });
            });
          }}
          onFail={(snapshot) => {
            void settleLevelRunTerminal({
              giantId,
              orderIndex,
              outcome: 'failed_minigame',
              snapshot,
            }).then(() => {
              nav.replace('failDelivery', { giantId, characterId, orderIndex });
            });
          }}
        />
      ) : null;
    case 'successDelivery':
      return giantId != null ? (
        <SuccessDeliveryScreen
          giantId={giantId}
          orderIndex={orderIndex}
          onViewResults={() => {
            const next = nextScreenAfterViewResults(unlockedStickers, deliveryParams);
            nav.replace(next.screen, next.params);
          }}
        />
      ) : null;
    case 'failDelivery':
      return (
        <FailDeliveryScreen
          characterId={characterId}
          onRestart={restartLevel}
          onGoHome={goHomeFromLevel}
        />
      );
    case 'deliveryResult':
      return (
        <DeliveryResultScreen
          giantId={giantId}
          orderIndex={orderIndex}
          showRestart={params?.hideRestart !== true}
          onRestart={restartLevel}
          onGoHome={
            params?.hideRestart === true ? () => nav.goBack() : goHomeFromLevel
          }
        />
      );
    case 'deliveryList':
      return (
        <DeliveryListScreen
          activeTab="deliveries"
          onTabChange={(tab) => handleHubTabChange(nav, tab)}
          onReplayDelivery={(replayGiantId, replayOrderIndex) => {
            void beginReplayDelivery(replayGiantId, replayOrderIndex).then((ok) => {
              if (!ok) {
                return;
              }
              nav.push('selectCharacter', {
                giantId: replayGiantId,
                orderIndex: replayOrderIndex,
              });
            });
          }}
          onOpenDetails={(detailsGiantId, detailsOrderIndex) =>
            nav.push('deliveryResult', {
              giantId: detailsGiantId,
              orderIndex: detailsOrderIndex,
              hideRestart: true,
            })
          }
        />
      );
    case 'stickers':
      return (
        <StickersScreen
          activeTab="rewards"
          onTabChange={(tab) => handleHubTabChange(nav, tab)}
        />
      );
    case 'stickerReward': {
      const resolved = resolveStickerRewardScreen(stickerId, deliveryParams);
      if (resolved.screen === 'deliveryResult') {
        return (
          <DeliveryResultScreen
            giantId={giantId}
            orderIndex={orderIndex}
            onRestart={restartLevel}
            onGoHome={goHomeFromLevel}
          />
        );
      }
      return (
        <StickerRewardScreen
          stickerId={stickerId!}
          onClaim={() => {
            const next = nextScreenAfterClaimSticker(remainingStickers, deliveryParams);
            nav.replace(next.screen, next.params);
          }}
        />
      );
    }
    case 'tickets':
      return (
        <TicketsScreen
          onGoBack={() => nav.goBack()}
          onOpenTasks={() => nav.push('taskList')}
        />
      );
    case 'taskList':
      return (
        <TaskListScreen
          onGoBack={() => nav.goBack()}
          onTaskClick={(taskIndex) => {
            if (taskIndex === TASK_INDEX_QUIZ) {
              nav.push('quiz');
              return;
            }
            const taskKind = taskKindFromIndex(taskIndex);
            if (taskKind != null) {
              nav.push('taskDetail', { taskKind });
            }
          }}
        />
      );
    case 'taskDetail': {
      const taskKind = params?.taskKind;
      if (taskKind == null) {
        return null;
      }
      return (
        <TaskDetailScreen
          taskKind={taskKind}
          onGoBack={() => nav.goBack()}
          onDailyLoginClaimed={(reward) => {
            nav.replace('dailyStrike', { rewardPasses: reward });
          }}
          onTaskCompleted={() => nav.goBack()}
        />
      );
    }
    case 'quiz':
      return <QuizScreen onGoBack={() => nav.goBack()} />;
    case 'dailyStrike':
      return (
        <DailyStrikeScreen
          rewardPasses={rewardPasses}
          onGoBack={() => nav.goBack()}
        />
      );
    case 'rating':
      return (
        <RatingScreen
          averageRating={ctx.totalRating}
          onGoBack={() => nav.goBack()}
        />
      );
    case 'playerLevel':
      return <PlayerLevelScreen onGoBack={() => nav.goBack()} />;
    default:
      return null;
  }
}

export function ScreenCoordinator() {
  const passesBalance = useAppStore((s) => s.passesBalance);
  const totalRating = useAppStore((s) => s.playerEconomy.totalRating);
  const audioLevel = useAppStore((s) => s.audioLevel);
  const nav = useScreenNavigation({ id: 'loading' });
  const screenId = nav.current.id;

  useEffect(() => {
    setBackgroundMusic(screenId === 'level' ? 'level' : 'map');
  }, [screenId]);

  return (
    <>
      <SyncWarningBanner />
      <ActionErrorBanner />
      {renderScreen(nav.current, nav, {
        passesBalance,
        totalRating,
      })}
      {nav.overlay?.id === 'pause' ? (
        <PauseScreen
          audioLevel={audioLevel}
          onCycleAudioLevel={() => {
            void useAppStore.getState().cycleAudioLevel();
          }}
          onContinue={() => nav.closeOverlay()}
          onGoHome={() => {
            void settleActiveLevelAbortedIfNeeded().then(() => {
              clearLevelAttempt();
              nav.replaceRoot('map');
            });
          }}
        />
      ) : null}
    </>
  );
}
